import { css } from "@emotion/css";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { UseNavigateResult, useNavigate } from "@tanstack/react-router";
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { TFunction } from "i18next";
import { DateTime } from "luxon";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { SearchForm, SearchTable } from "@/models/AnterioritySearchModel.ts";
import { Queries } from "@/queries/Queries.ts";
import type { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { Card } from "@/uiKit/atoms/Card.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { Form } from "@/uiKit/atoms/forms/Form.tsx";
import { Page } from "@/uiKit/atoms/page/Page.tsx";
import { PageContent } from "@/uiKit/atoms/page/PageContent.tsx";
import { PageHeader } from "@/uiKit/atoms/page/PageHeader.tsx";
import { ControlledTextField } from "@/uiKit/molecules/ControlledTextField.tsx";

type AnterioritySearchFormProps = {
  onSubmit: (data: SearchForm) => void;
};

const AnterioritySearchForm = ({ onSubmit }: AnterioritySearchFormProps) => {
  const form = useForm<SearchForm>();
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  return (
    <Form id="ANS_SEARCH" formContext={form} onSubmit={onSubmit}>
      <div className={styles.inline}>
        <ControlledTextField id="nir" name="nir" label={t("pages.search.form.NIR")} variant="standard" type="text" />
        <ControlledTextField
          id="names"
          name="names"
          label={t("pages.search.form.names")}
          variant="standard"
          type="text"
        />
        <Controller
          name="dob"
          render={({ field, fieldState }) => (
            <DatePicker
              label={t("pages.search.form.dob")}
              format="dd/MM/yyyy"
              value={field.value ? DateTime.fromISO(field.value) : null}
              onChange={date => field.onChange(date && date.isValid ? date.toISODate() : "")}
              className={styles.flexItem}
              slotProps={{
                textField: {
                  id: "dob",
                  variant: "standard",
                  fullWidth: true,
                  error: !!fieldState.error,
                },
              }}
            />
          )}
        />
      </div>

      <Button id="search" data-action="account.anteriority.search" variant="contained" type="submit">
        {t("pages.search.CTA")}
      </Button>
    </Form>
  );
};
const columnHelper = createColumnHelper<SearchTable>();

const getColumnDefinition = (t: TFunction, navigate: UseNavigateResult<string>) => {
  return [
    columnHelper.accessor("birthName", {
      header: t("pages.search.table.birthName"),
      cell: info => info.getValue(),
    }),

    columnHelper.accessor("usedLastName", {
      header: t("pages.search.table.usedLastName"),
      cell: info => info.getValue(),
    }),

    columnHelper.accessor("firstRegulatoryFirstName", {
      header: t("pages.search.table.firstRegulatoryFirstName"),
      cell: info => info.getValue(),
    }),

    columnHelper.accessor("usedFirstName", {
      header: t("pages.search.table.usedFirstName"),
      cell: info => info.getValue(),
    }),

    columnHelper.accessor("dob", {
      header: t("pages.search.table.dob"),
      cell: info => {
        const value = info.getValue();
        if (!value) return "";
        const parsed = DateTime.fromISO(value);
        return parsed.isValid ? parsed.toFormat("dd/MM/yyyy") : value;
      },
    }),

    columnHelper.accessor("gender", {
      header: t("pages.search.table.gender"),
      cell: info => info.getValue(),
    }),

    columnHelper.accessor("nir", {
      header: t("pages.search.table.nir"),
      cell: info => info.getValue(),
    }),

    columnHelper.display({
      id: "actions",
      header: "",
      cell: ({ row }) => {
        return (
          <Button
            data-action="account.anteriority.navigate-patient"
            variant="outlined"
            onClick={() => navigate({ to: `/patients/${row.original.id}` })}
          >
            {t("pages.search.table.CTA")}
          </Button>
        );
      },
    }),
  ];
};

type AnterioritySearchTableProps = {
  searchResults: SearchTable[];
};

const AnterioritySearchTable = ({ searchResults }: AnterioritySearchTableProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const styles = useStyles(makeStyles);

  const columns = getColumnDefinition(t, navigate);

  const table = useReactTable<SearchTable>({
    data: searchResults,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: false,
  });

  // TODO STE-1611 fix, use app table
  return (
    <table className={styles.fullWidth}>
      <TableHead>
        {table.getHeaderGroups().map(hg => (
          <TableRow key={hg.id}>
            {hg.headers.map(header => (
              <TableCell key={header.id}>{flexRender(header.column.columnDef.header, header.getContext())}</TableCell>
            ))}
          </TableRow>
        ))}
      </TableHead>
      <TableBody>
        {searchResults.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columns.length} align="center">
              {t("pages.search.table.noData")}
            </TableCell>
          </TableRow>
        ) : (
          table.getRowModel().rows.map(row => (
            <TableRow key={row.id} id={`anteriority-result-${row.original.id}`}>
              {row.getVisibleCells().map(cell => (
                <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
              ))}
            </TableRow>
          ))
        )}
      </TableBody>
    </table>
  );
};

export const PractitionerANSSearch = () => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  const searchMutation = Queries.practitioner.useAnterioritySearch();

  return (
    <Page id="practitioner-anteriority-search-page" className={styles.container}>
      <PageHeader title={t("pages.search.title")} className={styles.header} />
      <PageContent className={styles.content}>
        <Card>
          <CardContent>
            <AnterioritySearchForm onSubmit={searchMutation.mutate} />
            {searchMutation.data && <AnterioritySearchTable searchResults={searchMutation.data} />}
          </CardContent>
          <CardFooter />
        </Card>
      </PageContent>
    </Page>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({}),
  header: css({}),
  content: css({}),
  inline: css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(24)};
  `,
  flexItem: css`
    width: 100%;
  `,
  fullWidth: css`
    width: 100%;
    margin-top: ${theme.spacing(12)};
  `,
});
